import { useMemo } from 'react';
import { useHubAppCopy } from '../../hooks';

export interface TierPricing {
  tier: string;
  currency: string;
  amount: number;
  stripe_price_id: string;
  pricing_tier: number;
  requires_certification: boolean;
}

export interface PricingData {
  country_code: string;
  country_name: string;
  stripe_region: string;
  pricing_tier: number;
  currency: string;
  publishable_key: string;
  tiers: TierPricing[];
  user_certification_verified: boolean;
  invoice_payment_available?: boolean;
  trial_eligible?: boolean;
  invoice_days_until_due?: number;
  vat_collection_enabled?: boolean;
}

export interface PlanFeature {
  text: string;
  hasTooltip?: boolean;
}

export interface Plan {
  tier: string;
  name: string;
  description: string;
  features: PlanFeature[];
  eligibilityNote?: string;
}

interface PlanDefault {
  name: string;
  description: string;
  features: string[];
  eligibilityNote?: string;
}

// Feature definitions with keys for copy lookup and tooltip flags
const PLAN_FEATURE_DEFINITIONS = {
  A: [
    { key: 'feature1', hasTooltip: false },
    { key: 'feature2', hasTooltip: true },
    { key: 'feature3', hasTooltip: false },
    { key: 'feature4', hasTooltip: false },
  ],
  B: [
    { key: 'feature1', hasTooltip: false },
    { key: 'feature2', hasTooltip: true },
    { key: 'feature3', hasTooltip: false },
    { key: 'feature4', hasTooltip: true },
  ],
  C: [
    { key: 'feature1', hasTooltip: false },
    { key: 'feature2', hasTooltip: true },
    { key: 'feature3', hasTooltip: false },
    { key: 'feature4', hasTooltip: false },
  ],
}

// Default fallback values for plan display text
const PLAN_DEFAULTS: Record<'A' | 'B' | 'C', PlanDefault> = {
  A: {
    name: 'Starter',
    description: 'For individuals participating in change work or those making the case for better change management',
    features: ['Access to peer community', 'Best Practices in Change Management research', 'Four foundational eLearning modules', 'AI Integration Framework eLearning'],
  },
  B: {
    name: 'Essentials',
    description: 'For professionals establishing a formal process-driven and data-driven approach to change',
    features: ['All Starter benefits', 'Proxima cloud platform', 'Kaiya AI assistant (20 prompts/month)', 'Application guides and Pathways'],
    eligibilityNote: 'Must have completed Practitioner Program to be Eligible',
  },
  C: {
    name: 'Premier',
    description: 'For leaders looking to influence and advise on strategic change challenges',
    features: ['All Essentials benefits', 'Unlimited Kaiya access', 'Expert sessions with Prosci thought leaders', 'Exclusive AI, ERP, and portfolio research'],
    eligibilityNote: 'Must have completed Practitioner Program to be Eligible',
  },
}

export function useSubscriptionCopy() {
  const hubAppCopy = useHubAppCopy();
  return hubAppCopy('portalCopy')?.subscriptionCopy;
}

// Build plan definitions from copy with fallbacks
export function usePlanDefinitions(): Plan[] {
  const copy = useSubscriptionCopy();

  return useMemo(() => {
    const tiers = ['A', 'B', 'C'] as const;

    return tiers.map((tier) => {
      const planCopy = copy?.plans?.[tier];
      const defaults = PLAN_DEFAULTS[tier];
      const featureDefs = PLAN_FEATURE_DEFINITIONS[tier];

      // Build features array from copy with fallbacks
      const features = featureDefs.map((def, index) => ({
        text: planCopy?.features?.[def.key] || defaults.features[index],
        hasTooltip: def.hasTooltip
      }));

      return {
        tier,
        name: planCopy?.name || defaults.name,
        description: planCopy?.description || defaults.description,
        features,
        eligibilityNote: defaults.eligibilityNote
          ? planCopy?.eligibilityNote || defaults.eligibilityNote
          : undefined
      };
    });
  }, [copy]);
}
